import React, { useState } from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field, money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { HandCoins, Plus, Trash, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATS = ["Carrier Payout", "Driver Pay", "Fuel", "Insurance", "Office", "Software", "Factoring", "Other"];
const empty = { vendor: "", description: "", category: "Carrier Payout", amount: "", date: new Date().toISOString().slice(0, 10), due_date: "", status: "unpaid" };

export default function Payable() {
  const { data, isLoading } = useList("ap", "/accounting/payable");
  const { data: bills = [] } = useList("bills", "/bills");
  const invalidate = useInvalidate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const rows = data?.rows || [];
  const inp = "bg-white border-slate-300";

  const refresh = () => invalidate(["ap", "bills", "accounting-summary"]);

  const save = async () => {
    if (!form.vendor.trim() || !form.amount) { toast.error("Vendor and amount are required"); return; }
    setSaving(true);
    try { await api.post("/bills", { ...form, amount: Number(form.amount) || 0 }); toast.success("Bill added"); setOpen(false); setForm(empty); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const markPaid = async (id) => { await api.put(`/bills/${id}`, { status: "paid", paid_at: new Date().toISOString() }); toast.success("Bill marked paid"); refresh(); };
  const remove = async (id) => { if (!window.confirm("Delete this bill?")) return; await api.delete(`/bills/${id}`); refresh(); };

  return (
    <div>
      <PageHeader title="Accounts Payable" subtitle="Bills, carrier payouts and expenses you owe, by age"
        actions={<Button data-testid="add-bill-btn" onClick={() => { setForm(empty); setOpen(true); }} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"><Plus size={18} weight="bold" className="mr-1.5" />Add Bill</Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <Panel className="p-4"><p className="text-xs text-slate-500">Total Payable</p><p className="text-2xl font-heading font-extrabold text-red-600 tabular">{money(data?.total)}</p></Panel>
        <Panel className="p-4"><p className="text-xs text-slate-500">Current</p><p className="text-2xl font-heading font-extrabold text-emerald-600 tabular">{money(data?.buckets?.current)}</p></Panel>
        <Panel className="p-4"><p className="text-xs text-slate-500">1-30 days</p><p className="text-2xl font-heading font-extrabold text-amber-600 tabular">{money(data?.buckets?.["1-30"])}</p></Panel>
        <Panel className="p-4"><p className="text-xs text-slate-500">31-60 days</p><p className="text-2xl font-heading font-extrabold text-orange-600 tabular">{money(data?.buckets?.["31-60"])}</p></Panel>
        <Panel className="p-4"><p className="text-xs text-slate-500">60+ days</p><p className="text-2xl font-heading font-extrabold text-red-600 tabular">{money(data?.buckets?.["60+"])}</p></Panel>
      </div>

      <Panel className="p-0">
        {isLoading ? <p className="p-6 text-slate-400">Loading…</p> : rows.length === 0 ? (
          <EmptyState icon={CheckCircle} title="Nothing payable" hint="Add a bill or carrier payout to track what you owe." action={<Button onClick={() => setOpen(true)} className="bg-blue-600 text-white hover:bg-blue-700"><Plus size={16} className="mr-1" />Add Bill</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead>Vendor</TableHead><TableHead>Description</TableHead><TableHead>Category</TableHead><TableHead>Due</TableHead><TableHead>Age</TableHead><TableHead>Amount</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="border-slate-100" data-testid={`ap-row-${r.id}`}>
                    <TableCell className="font-medium text-slate-900">{r.vendor}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.description || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.category}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.due_date || "—"}</TableCell>
                    <TableCell><span className={`text-xs font-semibold ${r.bucket === "current" ? "text-slate-500" : "text-red-600"}`}>{r.bucket === "current" ? "Current" : r.bucket}</span></TableCell>
                    <TableCell className="font-mono font-semibold text-slate-900">{money(r.amount)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" onClick={() => markPaid(r.id)} className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid={`ap-paid-${r.id}`}>Pay</Button>
                        <button onClick={() => remove(r.id)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`ap-del-${r.id}`}><Trash size={16} /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><HandCoins size={20} weight="duotone" className="text-blue-600" />Add Bill / Payable</DialogTitle><DialogDescription className="sr-only">Record a bill or payout</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Vendor / Payee *"><Input data-testid="bill-vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className={inp} /></Field>
            <Field label="Description"><Input data-testid="bill-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inp} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className={inp} data-testid="bill-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Amount ($) *"><Input data-testid="bill-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={`${inp} font-mono`} /></Field>
              <Field label="Bill Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inp} /></Field>
              <Field label="Due Date"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inp} /></Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="bill-save-btn">{saving ? "Saving…" : "Add Bill"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
